import { useQuery } from '@tanstack/react-query';
import type { GridApi } from 'ag-grid-enterprise';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';

import type { QBFilter } from '@/components/QueryBuilder';
import type { TimePresetKeyWithField } from '@/components/TimeFilter';
import useHandleError from '@/hooks/useHandleError';

import type { VulnerabilityOrRemediationType } from '../VulnerabilityManagement.types';
import {
  addVMPECriticalities,
  replaceUnwantedValues
} from '../VulnerabilityManagement.utils';
import EMPTY_VULNERABILITY_PAGE from './VMPVulnerabilities.constants';

export const selectedVulnerabilityAtom = atom<VulnerabilityOrRemediationType>(
  EMPTY_VULNERABILITY_PAGE
);

export const vulnerabilitiesGridApiAtom = atom<GridApi | null>(null);

export const isGroupedByVulnerabilityAtom = atomWithStorage<boolean>(
  'isGroupedByVulnerability',
  false
);

export const vulnerabilitiesFiltersAtom = atom<QBFilter[] | null>();
export const vulnerabilitiesTimeFilterAtom =
  atom<TimePresetKeyWithField | null>(null);

export const useFetchVulnerabilityOrRemediation = (
  vulnerabilityId?: string
) => {
  const [, setSelectedVulnerability] = useAtom(selectedVulnerabilityAtom);

  const handleError = useHandleError();

  const { data, isError, error, refetch, isSuccess, isLoading } = useQuery({
    queryKey: [`v2/vulnerabilities/${vulnerabilityId}`],
    enabled: !!vulnerabilityId,
    staleTime: 60 * 1000 // 1 minute
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch vulnerability.');
    }
    if (isSuccess) {
      const formattedData = replaceUnwantedValues([
        data as VulnerabilityOrRemediationType
      ])[0];
      const vulnerabilityWithScore = addVMPECriticalities([formattedData])[0];
      setSelectedVulnerability(
        vulnerabilityWithScore as VulnerabilityOrRemediationType
      );
    }
  }, [isError, isSuccess, data]);

  return {
    isLoading,
    refetch
  };
};

export default selectedVulnerabilityAtom;
